import { ProjectLinkedModelInfoType } from '@/types/project-linked-models'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useDeleteDetail, {
  UseDeleteDetailOptions,
} from '../core-hooks/useDeleteDetail'
import useDeleteList, {
  UseDeleteListOptions,
} from '../core-hooks/useDeleteList'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import useUpdateList, {
  UseUpdateListOptions,
} from '../core-hooks/useUpdateList'
import buildService from '../create-service'
import queryKeys from '../query-keys'

const service = buildService<ProjectLinkedModelInfoType>({
  pathCategory: 'project_linked_model_info_types',
})

export function useGetProjectLinkedModelInfoType({
  id,
  ...options
}: UseGetDetailOptions<ProjectLinkedModelInfoType>) {
  const query = useGetDetail<ProjectLinkedModelInfoType>({
    id,
    queryKey: queryKeys.projectLinkedModelInfoTypes.detail({
      id,
      filters: options.filters,
    }),
    serviceFn: service.getDetail,
    ...options,
  })

  return {
    projectLinkedModelInfoType: query.data,
    ...query,
  }
}

export function useCreateProjectLinkedModelInfoType(
  options: UseCreateDetailOptions<ProjectLinkedModelInfoType> = {},
) {
  const mutation = useCreateDetail<ProjectLinkedModelInfoType>({
    serviceFn: service.createDetail,
    ...options,
  })

  return {
    createProjectLinkedModelInfoType: mutation.mutate,
    createProjectLinkedModelInfoTypeAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useUpdateProjectLinkedModelInfoType(
  options: UseUpdateDetailOptions<ProjectLinkedModelInfoType> = {},
) {
  const mutation = useUpdateDetail<ProjectLinkedModelInfoType>({
    serviceFn: service.updateDetail,
    ...options,
  })

  return {
    updateProjectLinkedModelInfoType: mutation.mutate,
    updateProjectLinkedModelInfoTypeAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteProjectLinkedModelInfoType(
  options: UseDeleteDetailOptions = {},
) {
  const mutation = useDeleteDetail<ProjectLinkedModelInfoType>({
    serviceFn: service.deleteDetail,
    ...options,
  })

  return {
    deleteProjectLinkedModelInfoType: mutation.mutate,
    deleteProjectLinkedModelInfoTypeAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useGetProjectLinkedModelInfoTypes({
  filters,
  ...options
}: UseGetListOptions<ProjectLinkedModelInfoType> = {}) {
  const query = useGetList<ProjectLinkedModelInfoType>({
    filters,
    queryKey: queryKeys.projectLinkedModelInfoTypes.list({ filters }),
    serviceFn: service.getList,
    ...options,
  })

  return {
    projectLinkedModelInfoTypes: query.allData,
    ...query,
  }
}

export function useUpdateProjectLinkedModelInfoTypes(
  options: UseUpdateListOptions<ProjectLinkedModelInfoType> = {},
) {
  const mutation = useUpdateList<ProjectLinkedModelInfoType>({
    serviceFn: service.updateList,
    ...options,
  })

  return {
    updateProjectLinkedModelInfoTypes: mutation.mutate,
    updateProjectLinkedModelInfoTypesAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteProjectLinkedModelInfoTypes({
  sideEffectQueryKeys,
  listQueryKey,
  ...options
}: UseDeleteListOptions = {}) {
  const mutation = useDeleteList<ProjectLinkedModelInfoType>({
    serviceFn: service.deleteList,
    sideEffectQueryKeys,
    listQueryKey,
    ...options,
  })

  return {
    deleteProjectLinkedModelInfoTypes: mutation.mutate,
    deleteProjectLinkedModelInfoTypesAsync: mutation.mutateAsync,
    ...mutation,
  }
}
